import { useCallback, useEffect, useState } from "react";
import { type AxiosError } from "axios";
import { axiosPost } from "@root/shared/utils/axiosForRails";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { ANYCAREER_TEL_NUMBER } from "@root/shared/utils/constants";

// サーバーからのレスポンス型
type ResponseCassetteProps = {
  informationSource: string;
  referenceNumber: string;
  title: string;
  businessTypeName: string;
  employmentName: string;
  facilityType: string;
  salaryTypeName: string;
  salaryMin: number | string;
  salaryMax: number | string;
  workPlaceAddress: string;
  nearestStation: string;
  imageUrl: string;
  detailUrl: string;
  showTelNumber: boolean;
};

// フロント用に拡張した型
type CassetteProps = Omit<ResponseCassetteProps, "showTelNumber"> & {
  telNumber: string;
  isShowTellButton: boolean;
};

type ResponseProps = {
  cassettes: ResponseCassetteProps[];
};

type UseAnyCareerWorkCassettesParams = {
  url: string;
  params: string[];
};

export const useAnyCareerWorkCassettes = ({
  url,
  params,
}: UseAnyCareerWorkCassettesParams): {
  anyCareerWorkRawWorks: CassetteProps[];
} => {
  const [worksData, setWorksData] = useState<CassetteProps[]>([]);

  const fetchWorksData = useCallback(async () => {
    if (!url || params.length === 0) {
      setWorksData((prev) => (prev.length ? [] : prev));
      return;
    }
    // NOTE: axiosPost は名前は POST 用だが、GET も利用できる共通インスタンス
    //       API 呼び出し用 hooks を一本化する方針のため、現状はこの形にしている
    //       今後整理時に命名や分離をリファクタリング予定です
    try {
      const res = await axiosPost.get<ResponseProps>(url, {
        params: { anycareer_work_ids: params },
        paramsSerializer: { indexes: false },
      });
      const cassettes = res.data.cassettes ?? [];
      // サーバーからのレスポンスに telNumber を追加し、showTelNumber を isShowTellButton にマッピング
      const cassettesWithTel: CassetteProps[] = cassettes.map((cassette) => {
        const { showTelNumber, ...rest } = cassette;
        return {
          ...rest,
          telNumber: ANYCAREER_TEL_NUMBER,
          isShowTellButton: showTelNumber,
        };
      });
      setWorksData(cassettesWithTel);
    } catch (e) {
      const error = e as AxiosError;
      sendErrorLog(
        new Error(error.message || "エニーキャリア求人カセットのデータを取得できませんでした"),
      );
    }
  }, [url, params]);

  useEffect(() => {
    fetchWorksData();
  }, [fetchWorksData]);

  return { anyCareerWorkRawWorks: worksData };
};
